export interface DateRange {
  startDate: string;
  endDate: string;
}

export function getToday(): Date {
  return new Date();
}

export function formatDateForInput(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export function formatDateForDisplay(dateStr: string): string {
  const date = new Date(dateStr);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export function getStartOfWeek(date: Date): Date {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day;
  return new Date(d.setDate(diff));
}

export function getEndOfWeek(date: Date): Date {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() + (6 - day);
  return new Date(d.setDate(diff));
}

export function getStartOfMonth(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), 1);
}

export function getEndOfMonth(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth() + 1, 0);
}

export function getStartOfYear(date: Date): Date {
  return new Date(date.getFullYear(), 0, 1);
}

export function getEndOfYear(date: Date): Date {
  return new Date(date.getFullYear(), 11, 31);
}

export function addDays(date: Date, days: number): Date {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
}

export function subtractDays(date: Date, days: number): Date {
  return addDays(date, -days);
}

export function getDateRangePreset(preset: string): DateRange {
  const today = getToday();
  let startDate: Date;
  let endDate: Date = today;

  switch (preset) {
    case 'today':
      startDate = today;
      break;
    case 'yesterday':
      startDate = subtractDays(today, 1);
      endDate = subtractDays(today, 1);
      break;
    case 'this_week':
      startDate = getStartOfWeek(today);
      endDate = getEndOfWeek(today);
      break;
    case 'last_week':
      const lastWeekStart = subtractDays(getStartOfWeek(today), 7);
      startDate = lastWeekStart;
      endDate = getEndOfWeek(lastWeekStart);
      break;
    case 'last_7_days':
      startDate = subtractDays(today, 6);
      break;
    case 'last_30_days':
      startDate = subtractDays(today, 29);
      break;
    case 'this_month':
      startDate = getStartOfMonth(today);
      endDate = getEndOfMonth(today);
      break;
    case 'last_month':
      const lastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      startDate = getStartOfMonth(lastMonth);
      endDate = getEndOfMonth(lastMonth);
      break;
    case 'this_year':
      startDate = getStartOfYear(today);
      endDate = getEndOfYear(today);
      break;
    case 'last_year':
      const lastYear = new Date(today.getFullYear() - 1, 0, 1);
      startDate = getStartOfYear(lastYear);
      endDate = getEndOfYear(lastYear);
      break;
    default:
      startDate = subtractDays(today, 29);
  }

  return {
    startDate: formatDateForInput(startDate),
    endDate: formatDateForInput(endDate),
  };
}

export function getDaysInMonth(year: number, month: number): number {
  return new Date(year, month + 1, 0).getDate();
}

export function getFirstDayOfMonth(year: number, month: number): number {
  return new Date(year, month, 1).getDay();
}

export function isDateInRange(dateStr: string, startStr: string, endStr: string): boolean {
  const date = new Date(dateStr);
  const start = new Date(startStr);
  const end = new Date(endStr);

  end.setHours(23, 59, 59, 999);

  return date >= start && date <= end;
}

export function isToday(dateStr: string): boolean {
  const date = new Date(dateStr);
  const today = getToday();
  return (
    date.getDate() === today.getDate() &&
    date.getMonth() === today.getMonth() &&
    date.getFullYear() === today.getFullYear()
  );
}

export function isSameDay(date1: Date, date2: Date): boolean {
  return (
    date1.getDate() === date2.getDate() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getFullYear() === date2.getFullYear()
  );
}

export function getMonthName(monthIndex: number): string {
  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];
  return months[monthIndex];
}

export function getShortMonthName(monthIndex: number): string {
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return months[monthIndex];
}

export function getStartOfDayUTC(dateStr: string): string {
  // Convert Eastern timezone date to UTC for database query
  // Eastern is UTC-5 (EST) or UTC-4 (EDT)
  const date = new Date(dateStr + 'T00:00:00');
  return date.toISOString();
}

export function getEndOfDayUTC(dateStr: string): string {
  // Convert Eastern timezone date to UTC for database query
  // Set to end of day (23:59:59.999) in Eastern time
  const date = new Date(dateStr + 'T23:59:59.999');
  return date.toISOString();
}
